import React, { useState, useEffect } from 'react';
import api from '../api';
import ConfirmDeleteModal from './ConfirmDeleteModal';

interface Department {
  id: number;
  name: string;
  code: string;
  abbreviation: string;
  description?: string;
  is_active: boolean;
  sort_order: number;
}

interface Category {
  id: number;
  name: string;
  department_id: number;
  abbreviation: string;
  description?: string;
  is_active: boolean;
  sort_order: number;
}

interface SubCategory {
  id: number;
  name: string;
  category_id: number;
  abbreviation: string;
  description?: string;
  is_active: boolean;
  sort_order: number;
}

type ViewMode = 'departments' | 'categories' | 'subcategories';

const TaxonomyManager: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('categories');
  
  // Data states
  const [departments, setDepartments] = useState<Department[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<SubCategory[]>([]);
  
  // Form states
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{show: boolean; id: number | null; name: string; type: string}>({show: false, id: null, name: '', type: ''});
  
  // Department form
  const [departmentForm, setDepartmentForm] = useState({
    name: '',
    code: '',
    abbreviation: '',
    description: '',
    is_active: true,
    sort_order: 0
  });
  
  // Category form
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    department_id: undefined as number | undefined,
    abbreviation: '',
    description: '',
    is_active: true,
    sort_order: 0,
    createNewDepartment: false,
    newDepartmentName: '',
    newDepartmentCode: '',
    newDepartmentAbbr: ''
  });
  
  // Subcategory form
  const [subcategoryForm, setSubcategoryForm] = useState({
    name: '',
    category_id: undefined as number | undefined,
    abbreviation: '',
    description: '',
    is_active: true,
    sort_order: 0
  });
  
  // For creating subcategories when creating a category
  const [newSubcategories, setNewSubcategories] = useState<Array<{
    name: string;
    abbreviation: string;
    description: string;
  }>>([]);

  // Sorting state for taxonomy tables
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIndicator = (column: string) => {
    if (sortColumn !== column) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const sortItems = <T,>(items: T[], getField: (item: T, col: string) => string | number): T[] => {
    if (!sortColumn) return items;
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...items].sort((a, b) => {
      const aVal = getField(a, sortColumn);
      const bVal = getField(b, sortColumn);
      if (typeof aVal === 'number' && typeof bVal === 'number') return dir * (aVal - bVal);
      return dir * String(aVal).localeCompare(String(bVal));
    });
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const [deptRes, catRes, subRes] = await Promise.all([
        api.get('/api/taxonomy/departments'),
        api.get('/api/taxonomy/categories'),
        api.get('/api/taxonomy/subcategories')
      ]);
      setDepartments(deptRes.data);
      setCategories(catRes.data);
      setSubcategories(subRes.data);
    } catch (error) {
      console.error('Error fetching taxonomy data:', error);
      alert('Failed to load taxonomy data');
    }
  };

  const getDepartmentName = (deptId: number) => {
    return departments.find(d => d.id === deptId)?.name || 'Unknown';
  };

  const getCategoryName = (catId: number) => {
    return categories.find(c => c.id === catId)?.name || 'Unknown';
  };

  const getSubcategoriesForCategory = (categoryId: number) => {
    return subcategories.filter(s => s.category_id === categoryId);
  };

  // Department handlers
  const handleDepartmentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/api/taxonomy/departments/${editingId}`, departmentForm);
        alert('Department updated successfully');
      } else {
        await api.post('/api/taxonomy/departments', departmentForm);
        alert('Department created successfully');
      }
      resetForms();
      fetchAllData();
    } catch (error: any) {
      console.error('Error saving department:', error);
      alert(error.response?.data?.detail || 'Failed to save department');
    }
  };

  // Category handlers
  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let departmentId = categoryForm.department_id;
      
      // Create new department if requested
      if (categoryForm.createNewDepartment) {
        const newDept = await api.post('/api/taxonomy/departments', {
          name: categoryForm.newDepartmentName,
          code: categoryForm.newDepartmentCode,
          abbreviation: categoryForm.newDepartmentAbbr,
          is_active: true,
          sort_order: 0
        });
        departmentId = newDept.data.id;
      }
      
      if (!departmentId) {
        alert('Please select or create a department');
        return;
      }
      
      const categoryData = {
        name: categoryForm.name,
        department_id: departmentId,
        abbreviation: categoryForm.abbreviation,
        description: categoryForm.description,
        is_active: categoryForm.is_active,
        sort_order: categoryForm.sort_order
      };
      
      let categoryId: number;
      
      if (editingId) {
        await api.put(`/api/taxonomy/categories/${editingId}`, categoryData);
        categoryId = editingId;
        alert('Category updated successfully');
      } else {
        const response = await api.post('/api/taxonomy/categories', categoryData);
        categoryId = response.data.id;
        
        // Create subcategories if any were added
        if (newSubcategories.length > 0) {
          for (const sub of newSubcategories) {
            await api.post('/api/taxonomy/subcategories', {
              name: sub.name,
              category_id: categoryId,
              abbreviation: sub.abbreviation,
              description: sub.description,
              is_active: true,
              sort_order: 0
            });
          }
        }
        
        alert('Category created successfully' + (newSubcategories.length > 0 ? ` with ${newSubcategories.length} subcategories` : ''));
      }
      
      resetForms();
      fetchAllData();
    } catch (error: any) {
      console.error('Error saving category:', error);
      alert(error.response?.data?.detail || 'Failed to save category');
    }
  };

  // Subcategory handlers
  const handleSubcategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!subcategoryForm.category_id) {
        alert('Please select a category');
        return;
      }
      
      if (editingId) {
        await api.put(`/api/taxonomy/subcategories/${editingId}`, subcategoryForm);
        alert('Subcategory updated successfully');
      } else {
        await api.post('/api/taxonomy/subcategories', subcategoryForm);
        alert('Subcategory created successfully');
      }
      resetForms();
      fetchAllData();
    } catch (error: any) {
      console.error('Error saving subcategory:', error);
      alert(error.response?.data?.detail || 'Failed to save subcategory');
    }
  };

  const resetForms = () => {
    setShowForm(false);
    setEditingId(null);
    setNewSubcategories([]);
    setDepartmentForm({
      name: '', code: '', abbreviation: '', description: '', is_active: true, sort_order: 0
    });
    setCategoryForm({
      name: '', department_id: undefined, abbreviation: '', description: '', 
      is_active: true, sort_order: 0, createNewDepartment: false,
      newDepartmentName: '', newDepartmentCode: '', newDepartmentAbbr: ''
    });
    setSubcategoryForm({
      name: '', category_id: undefined, abbreviation: '', description: '', 
      is_active: true, sort_order: 0
    });
  };

  const handleEditCategory = (category: Category) => {
    setCategoryForm({
      name: category.name,
      department_id: category.department_id,
      abbreviation: category.abbreviation,
      description: category.description || '',
      is_active: category.is_active,
      sort_order: category.sort_order,
      createNewDepartment: false,
      newDepartmentName: '',
      newDepartmentCode: '',
      newDepartmentAbbr: ''
    });
    setEditingId(category.id);
    setViewMode('categories');
    setShowForm(true);
  };

  const handleDeleteCategory = async (id: number, name?: string) => {
    const cat = categories.find(c => c.id === id);
    setDeleteConfirm({show: true, id, name: name || cat?.name || `Category #${id}`, type: 'category'});
  };
  
  const handleEditDepartment = (department: Department) => {
    setDepartmentForm({
      name: department.name,
      code: department.code,
      abbreviation: department.abbreviation,
      description: department.description || '',
      is_active: department.is_active,
      sort_order: department.sort_order
    });
    setEditingId(department.id);
    setViewMode('departments');
    setShowForm(true);
  };

  const handleDeleteDepartment = async (id: number, name?: string) => {
    const dept = departments.find(d => d.id === id);
    setDeleteConfirm({show: true, id, name: name || dept?.name || `Department #${id}`, type: 'department'});
  };
  
  const handleEditSubcategory = (subcategory: SubCategory) => {
    setSubcategoryForm({
      name: subcategory.name,
      category_id: subcategory.category_id,
      abbreviation: subcategory.abbreviation,
      description: subcategory.description || '',
      is_active: subcategory.is_active,
      sort_order: subcategory.sort_order
    });
    setEditingId(subcategory.id);
    setViewMode('subcategories');
    setShowForm(true);
  };

  const handleDeleteSubcategory = async (id: number, name?: string) => {
    const sub = subcategories.find(s => s.id === id);
    setDeleteConfirm({show: true, id, name: name || sub?.name || `Subcategory #${id}`, type: 'subcategory'});
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.id) return;
    try {
      if (deleteConfirm.type === 'category') {
        await api.delete(`/api/taxonomy/categories/${deleteConfirm.id}`);
      } else if (deleteConfirm.type === 'department') {
        await api.delete(`/api/taxonomy/departments/${deleteConfirm.id}`);
      } else if (deleteConfirm.type === 'subcategory') {
        await api.delete(`/api/taxonomy/subcategories/${deleteConfirm.id}`);
      }
      setDeleteConfirm({show: false, id: null, name: '', type: ''});
      fetchAllData();
    } catch (error: any) {
      console.error(`Error deleting ${deleteConfirm.type}:`, error);
      alert(error.response?.data?.detail || `Failed to delete ${deleteConfirm.type}`);
    }
  };

  return (
    <div className="content-card">
      <div className="card-header">
        <h2 className="card-title">Taxonomy Management</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button 
            className={`btn ${viewMode === 'departments' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('departments')}
          >
            Departments
          </button>
          <button 
            className={`btn ${viewMode === 'categories' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('categories')}
          >
            Categories
          </button>
          <button 
            className={`btn ${viewMode === 'subcategories' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('subcategories')}
          >
            Subcategories
          </button>
        </div>
      </div>

      <div style={{ padding: '1rem 2rem', borderBottom: '1px solid #e2e8f0' }}>
        <button 
          className="btn btn-primary" 
          onClick={() => {
            setShowForm(true);
            setEditingId(null);
          }}
        >
          Add {viewMode === 'departments' ? 'Department' : viewMode === 'categories' ? 'Category' : 'Subcategory'}
        </button>
      </div>

      {/* Department Form */}
      {showForm && viewMode === 'departments' && (
        <div style={{ padding: '1rem 2rem', borderBottom: '1px solid #e2e8f0', background: '#f9fafb' }}>
          <h3>{editingId ? 'Edit Department' : 'Add Department'}</h3>
          <form onSubmit={handleDepartmentSubmit}>
            <div className="form-group">
              <label className="form-label">Name *</label>
              <input
                type="text"
                className="form-input"
                value={departmentForm.name}
                onChange={(e) => setDepartmentForm({...departmentForm, name: e.target.value})}
                required
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Code *</label>
              <input
                type="text"
                className="form-input"
                value={departmentForm.code}
                onChange={(e) => setDepartmentForm({...departmentForm, code: e.target.value.toUpperCase()})}
                required
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Abbreviation (2 letters) *</label>
              <input
                type="text"
                className="form-input"
                value={departmentForm.abbreviation}
                onChange={(e) => setDepartmentForm({...departmentForm, abbreviation: e.target.value.toUpperCase()})}
                maxLength={2}
                required
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-textarea"
                value={departmentForm.description}
                onChange={(e) => setDepartmentForm({...departmentForm, description: e.target.value})}
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Sort Order</label>
              <input
                type="number"
                className="form-input"
                value={departmentForm.sort_order}
                onChange={(e) => setDepartmentForm({...departmentForm, sort_order: parseInt(e.target.value)})}
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">
                <input
                  type="checkbox"
                  checked={departmentForm.is_active}
                  onChange={(e) => setDepartmentForm({...departmentForm, is_active: e.target.checked})}
                  style={{ marginRight: '8px' }}
                />
                Active
              </label>
            </div>
            
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="submit" className="btn btn-primary">
                {editingId ? 'Update' : 'Create'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForms}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Category Form */}
      {showForm && viewMode === 'categories' && (
        <div style={{ padding: '1rem 2rem', borderBottom: '1px solid #e2e8f0', background: '#f9fafb' }}>
          <h3>{editingId ? 'Edit Category' : 'Add Category'}</h3>
          <form onSubmit={handleCategorySubmit}>
            <div className="form-group">
              <label className="form-label">Name *</label>
              <input
                type="text"
                className="form-input"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                required
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">
                <input
                  type="checkbox"
                  checked={categoryForm.createNewDepartment}
                  onChange={(e) => setCategoryForm({...categoryForm, createNewDepartment: e.target.checked})}
                  style={{ marginRight: '8px' }}
                />
                Create New Department
              </label>
            </div>
            
            {categoryForm.createNewDepartment ? (
              <>
                <div className="form-group">
                  <label className="form-label">New Department Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={categoryForm.newDepartmentName}
                    onChange={(e) => setCategoryForm({...categoryForm, newDepartmentName: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Department Code *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={categoryForm.newDepartmentCode}
                    onChange={(e) => setCategoryForm({...categoryForm, newDepartmentCode: e.target.value.toUpperCase()})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Department Abbreviation (2 letters) *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={categoryForm.newDepartmentAbbr}
                    onChange={(e) => setCategoryForm({...categoryForm, newDepartmentAbbr: e.target.value.toUpperCase()})}
                    maxLength={2}
                    required
                  />
                </div>
              </>
            ) : (
              <div className="form-group">
                <label className="form-label">Department *</label>
                <select
                  className="form-input"
                  value={categoryForm.department_id || ''}
                  onChange={(e) => setCategoryForm({...categoryForm, department_id: parseInt(e.target.value)})}
                  required
                >
                  <option value="">Select Department</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
              </div>
            )}
            
            <div className="form-group">
              <label className="form-label">Abbreviation (2 letters) *</label>
              <input
                type="text"
                className="form-input"
                value={categoryForm.abbreviation}
                onChange={(e) => setCategoryForm({...categoryForm, abbreviation: e.target.value.toUpperCase()})}
                maxLength={2}
                required
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-textarea"
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({...categoryForm, description: e.target.value})}
              />
            </div>
            
            {/* Subcategories section - only for new categories */}
            {!editingId && (
              <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f0f9ff', borderRadius: '4px' }}>
                <h4 style={{ marginBottom: '1rem' }}>Subcategories (Optional)</h4>
                {newSubcategories.map((sub, index) => (
                  <div key={index} style={{ marginBottom: '1rem', padding: '0.5rem', background: 'white', borderRadius: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong>{sub.name} ({sub.abbreviation})</strong>
                      <button
                        type="button"
                        className="btn btn-danger"
                        style={{ fontSize: '12px', padding: '2px 8px' }}
                        onClick={() => setNewSubcategories(newSubcategories.filter((_, i) => i !== index))}
                      >
                        Remove
                      </button>
                    </div>
                    {sub.description && <div style={{ fontSize: '12px', color: '#666' }}>{sub.description}</div>}
                  </div>
                ))}
                
                <div style={{ marginTop: '1rem' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      const name = prompt('Subcategory name:');
                      if (!name) return;
                      const abbreviation = prompt('Abbreviation (2 letters):');
                      if (!abbreviation || abbreviation.length !== 2) {
                        alert('Abbreviation must be exactly 2 letters');
                        return;
                      }
                      const description = prompt('Description (optional):') || '';
                      setNewSubcategories([...newSubcategories, {
                        name,
                        abbreviation: abbreviation.toUpperCase(),
                        description
                      }]);
                    }}
                  >
                    + Add Subcategory
                  </button>
                </div>
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '8px', marginTop: '1rem' }}>
              <button type="submit" className="btn btn-primary">
                {editingId ? 'Update' : 'Create'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForms}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Subcategory Form */}
      {showForm && viewMode === 'subcategories' && (
        <div style={{ padding: '1rem 2rem', borderBottom: '1px solid #e2e8f0', background: '#f9fafb' }}>
          <h3>{editingId ? 'Edit Subcategory' : 'Add Subcategory'}</h3>
          <form onSubmit={handleSubcategorySubmit}>
            <div className="form-group">
              <label className="form-label">Name *</label>
              <input
                type="text"
                className="form-input"
                value={subcategoryForm.name}
                onChange={(e) => setSubcategoryForm({...subcategoryForm, name: e.target.value})}
                required
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Category *</label>
              <select
                className="form-input"
                value={subcategoryForm.category_id || ''}
                onChange={(e) => setSubcategoryForm({...subcategoryForm, category_id: parseInt(e.target.value)})}
                required
              >
                <option value="">Select Category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name} ({getDepartmentName(cat.department_id)})
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label className="form-label">Abbreviation (2 letters) *</label>
              <input
                type="text"
                className="form-input"
                value={subcategoryForm.abbreviation}
                onChange={(e) => setSubcategoryForm({...subcategoryForm, abbreviation: e.target.value.toUpperCase()})}
                maxLength={2}
                required
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-textarea"
                value={subcategoryForm.description}
                onChange={(e) => setSubcategoryForm({...subcategoryForm, description: e.target.value})}
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">
                <input
                  type="checkbox"
                  checked={subcategoryForm.is_active}
                  onChange={(e) => setSubcategoryForm({...subcategoryForm, is_active: e.target.checked})}
                  style={{ marginRight: '8px' }}
                />
                Active
              </label>
            </div>
            
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="submit" className="btn btn-primary">
                {editingId ? 'Update' : 'Create'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForms}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Departments View */}
      {viewMode === 'departments' && !showForm && (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('name')}>Name{getSortIndicator('name')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('code')}>Code{getSortIndicator('code')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('abbreviation')}>Abbreviation{getSortIndicator('abbreviation')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('catCount')}>Categories Count{getSortIndicator('catCount')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('is_active')}>Active{getSortIndicator('is_active')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('sort_order')}>Sort Order{getSortIndicator('sort_order')}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortItems(departments, (dept, col) => {
                switch (col) {
                  case 'name': return dept.name;
                  case 'code': return dept.code;
                  case 'abbreviation': return dept.abbreviation;
                  case 'catCount': return categories.filter(c => c.department_id === dept.id).length;
                  case 'is_active': return dept.is_active ? 'Yes' : 'No';
                  case 'sort_order': return dept.sort_order;
                  default: return '';
                }
              }).map(dept => {
                const catCount = categories.filter(c => c.department_id === dept.id).length;
                return (
                  <tr key={dept.id}>
                    <td>{dept.name}</td>
                    <td>{dept.code}</td>
                    <td>{dept.abbreviation}</td>
                    <td>{catCount}</td>
                    <td>{dept.is_active ? '✓' : '✗'}</td>
                    <td>{dept.sort_order}</td>
                    <td>
                      <button 
                        className="btn btn-secondary" 
                        onClick={() => handleEditDepartment(dept)}
                        style={{ fontSize: '12px', padding: '4px 8px', marginRight: '8px' }}
                      >
                        Edit
                      </button>
                      <button 
                        className="btn btn-danger" 
                        onClick={() => handleDeleteDepartment(dept.id, dept.name)}
                        style={{ fontSize: '12px', padding: '4px 8px' }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Categories View */}
      {viewMode === 'categories' && !showForm && (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('name')}>Name{getSortIndicator('name')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('department')}>Department{getSortIndicator('department')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('abbreviation')}>Abbreviation{getSortIndicator('abbreviation')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('subCount')}>Subcategories{getSortIndicator('subCount')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('is_active')}>Active{getSortIndicator('is_active')}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortItems(categories, (cat, col) => {
                switch (col) {
                  case 'name': return cat.name;
                  case 'department': return getDepartmentName(cat.department_id);
                  case 'abbreviation': return cat.abbreviation;
                  case 'subCount': return getSubcategoriesForCategory(cat.id).length;
                  case 'is_active': return cat.is_active ? 'Yes' : 'No';
                  default: return '';
                }
              }).map(category => {
                const subs = getSubcategoriesForCategory(category.id);
                return (
                  <tr key={category.id}>
                    <td>{category.name}</td>
                    <td>{getDepartmentName(category.department_id)}</td>
                    <td>{category.abbreviation}</td>
                    <td>
                      {subs.length > 0 ? (
                        <div style={{ fontSize: '12px' }}>
                          {subs.map(s => s.name).join(', ')}
                        </div>
                      ) : (
                        <span style={{ color: '#999' }}>None</span>
                      )}
                    </td>
                    <td>{category.is_active ? '✓' : '✗'}</td>
                    <td>
                      <button 
                        className="btn btn-secondary" 
                        onClick={() => handleEditCategory(category)}
                        style={{ fontSize: '12px', padding: '4px 8px', marginRight: '8px' }}
                      >
                        Edit
                      </button>
                      <button 
                        className="btn btn-danger" 
                        onClick={() => handleDeleteCategory(category.id, category.name)}
                        style={{ fontSize: '12px', padding: '4px 8px' }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Subcategories View */}
      {viewMode === 'subcategories' && !showForm && (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('name')}>Name{getSortIndicator('name')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('category')}>Category{getSortIndicator('category')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('department')}>Department{getSortIndicator('department')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('abbreviation')}>Abbreviation{getSortIndicator('abbreviation')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('is_active')}>Active{getSortIndicator('is_active')}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortItems(subcategories, (sub, col) => {
                switch (col) {
                  case 'name': return sub.name;
                  case 'category': return getCategoryName(sub.category_id);
                  case 'department': {
                    const cat = categories.find(c => c.id === sub.category_id);
                    return cat ? getDepartmentName(cat.department_id) : '';
                  }
                  case 'abbreviation': return sub.abbreviation;
                  case 'is_active': return sub.is_active ? 'Yes' : 'No';
                  default: return '';
                }
              }).map(sub => {
                const category = categories.find(c => c.id === sub.category_id);
                const deptId = category?.department_id;
                return (
                  <tr key={sub.id}>
                    <td>{sub.name}</td>
                    <td>{getCategoryName(sub.category_id)}</td>
                    <td>{deptId ? getDepartmentName(deptId) : '-'}</td>
                    <td>{sub.abbreviation}</td>
                    <td>{sub.is_active ? '✓' : '✗'}</td>
                    <td>
                      <button 
                        className="btn btn-secondary" 
                        onClick={() => handleEditSubcategory(sub)}
                        style={{ fontSize: '12px', padding: '4px 8px', marginRight: '8px' }}
                      >
                        Edit
                      </button>
                      <button 
                        className="btn btn-danger" 
                        onClick={() => handleDeleteSubcategory(sub.id, sub.name)}
                        style={{ fontSize: '12px', padding: '4px 8px' }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <ConfirmDeleteModal
        isOpen={deleteConfirm.show}
        title={`Delete ${deleteConfirm.type === 'department' ? 'Department' : deleteConfirm.type === 'category' ? 'Category' : 'Subcategory'}`}
        message={
          deleteConfirm.type === 'department'
            ? 'Are you sure? This will also delete all categories and subcategories under this department.'
            : deleteConfirm.type === 'category'
            ? 'Are you sure? This will also delete all subcategories under this category.'
            : 'Are you sure you want to delete this subcategory? This action cannot be undone.'
        }
        itemName={deleteConfirm.name}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({show: false, id: null, name: '', type: ''})}
      />
    </div>
  );
};

export default TaxonomyManager;
